import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Unlock, Download, AlertTriangle, ShieldAlert, History, Pencil, FileSpreadsheet } from "lucide-react";

const BUCKET_STYLES = {
  hourly: "bg-sky-100 text-sky-800",
  commission: "bg-purple-100 text-purple-800",
  hourly_plus_commission: "bg-indigo-100 text-indigo-800",
  manual_override: "bg-amber-100 text-amber-900",
};

function bucketLabel(b) {
  return (b || "").replace(/_/g, " ").toUpperCase();
}

export default function PayrollExportTab() {
  const [weekStart, setWeekStart] = useState(todayISO());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formats, setFormats] = useState([]);
  const [format, setFormat] = useState("standard");
  const [history, setHistory] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [adjustOpen, setAdjustOpen] = useState(null); // { employee_id, employee_name, before }
  const [adjustValue, setAdjustValue] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const loadFormats = useCallback(async () => {
    const { data } = await api.get("/payroll-export/formats");
    setFormats(data.formats || []);
    setDisclaimer(data.disclaimer || "");
  }, []);

  const loadPreview = useCallback(async (ws) => {
    setBusy(true);
    try {
      const { data } = await api.get("/payroll-export/preview", { params: { week_start: ws } });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/payroll-export/history");
    setHistory(data.runs || []);
  }, []);

  useEffect(() => { loadFormats(); loadHistory(); }, [loadFormats, loadHistory]);
  useEffect(() => { loadPreview(weekStart); }, [weekStart, loadPreview]);

  const status = preview?.period?.status || "draft";
  const isApproved = status === "approved";
  const rows = preview?.rows || [];
  const totals = preview?.totals || {};

  const handleExport = async () => {
    if (!rows.length) {
      toast.error("Nothing to export — no employees with hours/production this week.");
      return;
    }
    try {
      const resp = await api.post(
        "/payroll-export/export",
        { week_start: weekStart, format },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = `payroll-${preview.week_start}-to-${preview.week_end}-${format}.csv`;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()} CSV`);
      await loadHistory();
    } catch (e) {
      // Blob responses don't auto-parse — read it as text first
      let msg = e.message;
      try {
        if (e.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          msg = JSON.parse(t).detail || msg;
        } else {
          msg = formatApiError(e.response?.data?.detail) || msg;
        }
      } catch { /* swallow */ }
      toast.error(msg);
    }
  };

  const handleApprove = async () => {
    try {
      await api.post("/payroll-export/approve", { week_start: weekStart, notes: approveNotes });
      toast.success("Pay period approved & locked");
      setApproveOpen(false);
      setApproveNotes("");
      await loadPreview(weekStart);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleReopen = async () => {
    if (reopenReason.trim().length < 2) { toast.error("Reason required"); return; }
    try {
      await api.post("/payroll-export/reopen", { week_start: weekStart, reason: reopenReason });
      toast.success("Pay period reopened");
      setReopenOpen(false);
      setReopenReason("");
      await loadPreview(weekStart);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleAdjust = async () => {
    if (!adjustOpen) return;
    if (adjustReason.trim().length < 2) { toast.error("Reason required for an adjustment"); return; }
    const val = adjustValue === "" ? null : Number(adjustValue);
    if (val !== null && Number.isNaN(val)) { toast.error("Final gross must be a number"); return; }
    try {
      await api.post("/payroll-export/adjust", {
        week_start: weekStart,
        employee_id: adjustOpen.employee_id,
        final_gross_override: val,
        reason: adjustReason,
      });
      toast.success("Adjustment recorded");
      setAdjustOpen(null);
      setAdjustValue("");
      setAdjustReason("");
      await loadPreview(weekStart);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const reDownload = async (run) => {
    try {
      const resp = await api.get(`/payroll-export/history/${run.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = run.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="payroll-export-tab">
      {/* Disclaimer banner */}
      <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="payroll-export-disclaimer">
        <CardContent className="p-4 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-amber-900">
            {disclaimer || "Production pay tracker — not a payroll provider. Final payroll processing, tax filings, direct deposit, W-2s, and 1099s must be completed through a licensed payroll provider or tax professional."}
          </p>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Pay period (any day)</Label>
            <Input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="w-48"
              data-testid="export-week-input"
            />
            {preview && (
              <p className="text-xs text-slate-500 mt-1">{fmtDate(preview.week_start)} → {fmtDate(preview.week_end)}</p>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Export format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="w-64" data-testid="export-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {formats.map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`format-option-${f.id}`}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleExport}
            disabled={busy || rows.length === 0}
            className="bg-sky-600 hover:bg-sky-700 h-10"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export Payroll CSV
          </Button>
          {!isApproved ? (
            <Button
              onClick={() => setApproveOpen(true)}
              disabled={busy || rows.length === 0}
              variant="outline"
              className="h-10 border-green-300 text-green-800 hover:bg-green-50"
              data-testid="approve-period-btn"
            >
              <Lock className="h-4 w-4 mr-1.5" /> Approve Pay Period
            </Button>
          ) : (
            <Button
              onClick={() => setReopenOpen(true)}
              variant="outline"
              className="h-10 border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="reopen-period-btn"
            >
              <Unlock className="h-4 w-4 mr-1.5" /> Reopen Pay Period
            </Button>
          )}
          <div className="ml-auto">
            {isApproved && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800" data-testid="period-status-approved">
                <Lock className="h-3 w-3" /> Approved {preview?.period?.approved_at ? `· ${fmtDate(preview.period.approved_at)}` : ""}
              </span>
            )}
          </div>
          {preview && rows.length === 0 && (
            <p className="basis-full text-xs text-slate-500 mt-1" data-testid="export-empty-hint">
              No active employees have hours or production for this week yet. Once punches or jobs are recorded, Export &amp; Approve will be enabled.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Totals */}
      {preview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Employees" value={totals.employee_count || 0} />
          <Stat label="Total Gross" value={fmtMoney(totals.total_gross || 0)} accent />
          <Stat label="Reg / OT Hours" value={`${(totals.total_regular_hours || 0).toFixed(1)} / ${(totals.total_overtime_hours || 0).toFixed(1)}`} />
          <Stat label="Est. Net Pay" value={fmtMoney(totals.total_estimated_net || 0)} />
        </div>
      )}

      {/* Review table */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-3 font-semibold">Employee</th>
                <th className="text-right px-3 py-3 font-semibold">Reg / OT</th>
                <th className="text-right px-3 py-3 font-semibold">Hourly Gross</th>
                <th className="text-right px-3 py-3 font-semibold">Invoice</th>
                <th className="text-right px-3 py-3 font-semibold">Comm %</th>
                <th className="text-right px-3 py-3 font-semibold">Comm Gross</th>
                <th className="text-right px-3 py-3 font-semibold">Reimb</th>
                <th className="text-center px-3 py-3 font-semibold">Winner</th>
                <th className="text-right px-3 py-3 font-semibold">Final Gross</th>
                <th className="text-right px-3 py-3 font-semibold">Est. Net</th>
                <th className="text-center px-3 py-3 font-semibold">Adj</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const bucketCls = BUCKET_STYLES[r.pay_bucket_winner] || BUCKET_STYLES.hourly;
                return (
                  <tr key={r.employee_id} className="border-b border-slate-100" data-testid={`export-row-${r.employee_id}`}>
                    <td className="px-3 py-2.5 font-semibold text-slate-900">
                      {r.employee_name}
                      <div className="text-[10px] text-slate-400">{r.employee_email}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right">{r.regular_hours.toFixed(1)} / {r.overtime_hours.toFixed(1)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtMoney(r.hourly_gross_pay)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtMoney(r.gross_invoice_total)}</td>
                    <td className="px-3 py-2.5 text-right">{r.commission_percentage}%</td>
                    <td className="px-3 py-2.5 text-right">{fmtMoney(r.commission_gross_pay)}</td>
                    <td className="px-3 py-2.5 text-right">{fmtMoney(r.reimbursements)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider ${bucketCls}`}>
                        {bucketLabel(r.pay_bucket_winner)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-base font-extrabold text-green-700">{fmtMoney(r.final_gross_pay)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-700">{r.taxes_enabled ? fmtMoney(r.estimated_net_pay) : "—"}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => { setAdjustOpen({ employee_id: r.employee_id, employee_name: r.employee_name, before: r.final_gross_pay }); setAdjustValue(""); setAdjustReason(""); }}
                        data-testid={`adjust-btn-${r.employee_id}`}
                        title="Manual adjustment"
                      >
                        <Pencil className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan="11" className="px-4 py-10 text-center text-slate-500">No employees with hours/production this week.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Export history */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 mb-3">
          <History className="h-4 w-4" /> Export History
        </h2>
        <Card className="rounded-2xl border-slate-200">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3 font-semibold">Pay Period</th>
                  <th className="text-left px-3 py-3 font-semibold">Format</th>
                  <th className="text-left px-3 py-3 font-semibold">Exported</th>
                  <th className="text-left px-3 py-3 font-semibold">Exported By</th>
                  <th className="text-right px-3 py-3 font-semibold">Employees</th>
                  <th className="text-right px-3 py-3 font-semibold">Total Gross</th>
                  <th className="text-right px-3 py-3 font-semibold">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {history.map((run) => (
                  <tr key={run.id} className="border-b border-slate-100" data-testid={`history-row-${run.id}`}>
                    <td className="px-3 py-2.5">{fmtDate(run.week_start)} → {fmtDate(run.week_end)}</td>
                    <td className="px-3 py-2.5">{run.format_label}</td>
                    <td className="px-3 py-2.5">{new Date(run.exported_at).toLocaleString()}</td>
                    <td className="px-3 py-2.5">{run.exported_by?.name || run.exported_by?.email || "—"}</td>
                    <td className="px-3 py-2.5 text-right">{run.employee_count}</td>
                    <td className="px-3 py-2.5 text-right font-bold text-green-700">{fmtMoney(run.total_gross)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Button variant="outline" size="sm" onClick={() => reDownload(run)} data-testid={`redownload-${run.id}`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Download Again
                      </Button>
                    </td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-6 text-center text-slate-500">No exports yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Adjust dialog */}
      <Dialog open={!!adjustOpen} onOpenChange={(open) => { if (!open) setAdjustOpen(null); }}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Manual Adjustment — {adjustOpen?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Original gross pay: <span className="font-bold text-slate-900">{fmtMoney(adjustOpen?.before || 0)}</span></div>
            <div>
              <Label>Final gross override (leave blank to clear an override)</Label>
              <Input
                type="number"
                step="0.01"
                value={adjustValue}
                onChange={(e) => setAdjustValue(e.target.value)}
                placeholder="e.g. 950.00"
                data-testid="adjust-amount"
              />
            </div>
            <div>
              <Label>Reason (required, audit-logged)</Label>
              <Input
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="e.g. Add $50 retroactive bonus"
                data-testid="adjust-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(null)}>Cancel</Button>
            <Button onClick={handleAdjust} className="bg-sky-600 hover:bg-sky-700" data-testid="adjust-save-btn">Save Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Approve & Lock Pay Period</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Once approved, employees can no longer edit their time entries, jobs, or submissions for this week. You can still adjust through Manual Adjustments. The period can be reopened any time.</span>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} placeholder="e.g. Week 23, 2026" data-testid="approve-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700" data-testid="approve-confirm-btn">
              <Lock className="h-4 w-4 mr-1" /> Approve & Lock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent data-testid="reopen-dialog">
          <DialogHeader><DialogTitle>Reopen Pay Period</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason for reopen (required, audit-logged)</Label>
              <Input value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="e.g. Late job entry from Friday" data-testid="reopen-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)}>Cancel</Button>
            <Button onClick={handleReopen} className="bg-amber-600 hover:bg-amber-700" data-testid="reopen-confirm-btn">
              <Unlock className="h-4 w-4 mr-1" /> Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <Card className="rounded-2xl border-slate-200">
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
        <p className={`text-2xl font-extrabold font-display mt-1 ${accent ? "text-green-700" : "text-slate-900"}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
